import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const MIME_TYPE_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

function pickSupportedMimeType() {
  if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
    return "";
  }

  if (typeof window.MediaRecorder.isTypeSupported !== "function") {
    return "";
  }

  return MIME_TYPE_CANDIDATES.find((candidate) =>
    window.MediaRecorder.isTypeSupported(candidate),
  ) || "";
}

function extensionForMimeType(mimeType) {
  if (`${mimeType}`.includes("mp4")) return "m4a";
  if (`${mimeType}`.includes("ogg")) return "ogg";
  if (`${mimeType}`.includes("wav")) return "wav";
  return "webm";
}

export default function useVoiceNoteRecorder({ onError } = {}) {
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(0);
  const timerRef = useRef(null);
  const discardOnStopRef = useRef(false);
  const previewUrlRef = useRef("");

  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewDurationMs, setPreviewDurationMs] = useState(0);

  const isSupported = useMemo(() => {
    return (
      typeof window !== "undefined" &&
      typeof navigator !== "undefined" &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === "function" &&
      typeof window.MediaRecorder !== "undefined"
    );
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }
    setPreviewFile(null);
    setPreviewUrl("");
    setPreviewDurationMs(0);
  }, []);

  const resetRecorder = useCallback(() => {
    recorderRef.current = null;
    chunksRef.current = [];
    startTimeRef.current = 0;
    discardOnStopRef.current = false;
    stopTimer();
    releaseStream();
    setIsRecording(false);
    setElapsedMs(0);
  }, [releaseStream, stopTimer]);

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      onError?.("Voice notes are not supported in this browser.");
      return;
    }
    if (isRecording || isProcessing) return;

    clearPreview();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickSupportedMimeType();
      const recorder = mimeType
        ? new window.MediaRecorder(stream, { mimeType })
        : new window.MediaRecorder(stream);

      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];
      startTimeRef.current = Date.now();
      discardOnStopRef.current = false;
      setElapsedMs(0);
      setIsProcessing(false);

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = () => {
        onError?.("Voice note recording failed.");
        resetRecorder();
      };

      recorder.onstop = () => {
        const durationMs = Math.max(0, Date.now() - startTimeRef.current);
        const shouldDiscard = discardOnStopRef.current;
        const nextMimeType = recorder.mimeType || mimeType || "audio/webm";
        const chunks = [...chunksRef.current];

        resetRecorder();

        if (shouldDiscard || chunks.length === 0 || durationMs < 300) {
          setIsProcessing(false);
          return;
        }

        const extension = extensionForMimeType(nextMimeType);
        const blob = new Blob(chunks, { type: nextMimeType });
        const file = new File([blob], `voice-note-${Date.now()}.${extension}`, {
          type: nextMimeType,
        });
        const nextPreviewUrl = URL.createObjectURL(blob);
        previewUrlRef.current = nextPreviewUrl;
        setPreviewFile(file);
        setPreviewUrl(nextPreviewUrl);
        setPreviewDurationMs(durationMs);
        setIsProcessing(false);
      };

      recorder.start(250);
      setIsRecording(true);
      timerRef.current = window.setInterval(() => {
        setElapsedMs(Math.max(0, Date.now() - startTimeRef.current));
      }, 200);
    } catch (_) {
      setIsProcessing(false);
      resetRecorder();
      onError?.("Microphone access was blocked. Allow microphone access to record a voice note.");
    }
  }, [clearPreview, isProcessing, isRecording, isSupported, onError, resetRecorder]);

  const stopRecording = useCallback(() => {
    if (!recorderRef.current || recorderRef.current.state === "inactive") return;
    setIsProcessing(true);
    discardOnStopRef.current = false;
    recorderRef.current.stop();
  }, []);

  const cancelRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      discardOnStopRef.current = true;
      recorderRef.current.stop();
      return;
    }

    clearPreview();
    resetRecorder();
    setIsProcessing(false);
  }, [clearPreview, resetRecorder]);

  useEffect(() => {
    return () => {
      clearPreview();
      resetRecorder();
      setIsProcessing(false);
    };
  }, [clearPreview, resetRecorder]);

  return {
    isSupported,
    isRecording,
    isProcessing,
    elapsedMs,
    previewFile,
    previewUrl,
    previewDurationMs,
    startRecording,
    stopRecording,
    cancelRecording,
    clearPreview,
  };
}
